import { Component, ElementRef, ViewChild, inject, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BiqStorageService } from '@app/services/local-storage/biq-storage.service';
import { StartPageComponent } from '@app/shared/biq/start-page/start-page.component';
import { LocationComponent } from '@app/shared/biq/location/location.component';
import { BirthDateComponent } from '@app/shared/biq/birth-date/birth-date.component';
import { GenderComponent } from '@app/shared/biq/gender/gender.component';
import { EthnicityComponent } from '@app/shared/biq/ethnicity/ethnicity.component';
import { EmploymentComponent } from '@app/shared/biq/employment/employment.component';
import { DesiredLocationComponent } from '@app/shared/biq/desired-location/desired-location.component';
import { EndPageComponent } from '@app/shared/biq/end-page/end-page.component';
import { Router, ActivatedRoute } from '@angular/router';
import { UserBiqService } from '@app/services/api/user-biq/user-biq.service';
import { UserBiqData } from '@app/schemas/biq';
import { Store } from '@ngrx/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { updateUserBiq } from '@app/state/actions/biq.action';
import { UserService } from '@app/services/api/user/user.service';
import { WarningSnackBarComponent } from '@app/shared/snack-bars/warning/warning.component';
import { WarningDefaultOptions } from '@app/shared/snack-bars/warning/warning.utils';
import { FullnameComponent } from '@app/shared/biq/fullname/fullname.component';
import { analytics } from '@app/events/segment/analytics';

@Component({
  selector: 'app-user-biq',
  imports: [
    StartPageComponent,
    LocationComponent,
    BirthDateComponent,
    GenderComponent,
    EthnicityComponent,
    EmploymentComponent,
    DesiredLocationComponent,
    EndPageComponent,
    FullnameComponent,
  ],
  templateUrl: './user-biq.component.html',
  styleUrl: './user-biq.component.css'
})
export class UserBiqComponent implements OnInit {
  @ViewChild('contentDiv') contentDiv!: ElementRef<HTMLDivElement>;
  private scrollTimeout: any;
  public showFullname: boolean = false;
  public showLocation: boolean = false;
  public showDOB: boolean = false;
  public showGender: boolean = false;
  public showEthnicity: boolean = false;
  public showEmployment: boolean = false;
  public showDesiredLocation: boolean = false;
  public showEndPage: boolean = false;
  public storedBiqData!: UserBiqData;
  private _snackBar = inject(MatSnackBar);
  public currentStep: number = 0;
  private completedSteps: Set<number> = new Set();
  private biqStorageService = inject(BiqStorageService);
  private readonly LOCALSTORAGE_KEY = 'user-biq-responses';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private biqApiService: UserBiqService,
    private store: Store<{ userBiq: UserBiqData }>,
    private userService: UserService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.store.select('userBiq').subscribe(value => {
      this.storedBiqData = value;
      if (value && (value.firstName || value.location || value.birthYear)) {
        this.saveToLocalStorage(value);
      }
    });
  }

  ngOnInit() {
    this.loadFromLocalStorage();
    this.completedSteps = new Set(this.biqStorageService.loadCompletedSteps());
    this.route.params.subscribe(params => {
      const step = params['step'] ? parseInt(params['step']) : 0;
      // Add small delay to ensure localStorage data is loaded and store is updated
      setTimeout(() => {
        this.setCurrentStep(step);
      }, 0);
    });
  }

  private setCurrentStep(step: number) {
    // Guard: prevent skipping ahead
    const firstIncomplete = this.getFirstIncompleteStep(step);
    if (firstIncomplete !== null) {
      this.navigateToStep(firstIncomplete);
      return;
    }

    this.currentStep = step;
    this.resetAllSteps();
    
    switch (step) {
      case 1:
        this.showFullname = true;
        break;
      case 2:
        this.showLocation = true;
        break;
      case 3:
        this.showDOB = true;
        break;
      case 4:
        this.showGender = true;
        break;
      case 5:
        this.showEthnicity = true;
        break;
      case 6:
        this.showEmployment = true;
        break;
      case 7:
        this.showDesiredLocation = true;
        break;
      case 8:
        this.showEndPage = true;
        break;
      default:
        break;
    }
  }

  private resetAllSteps() {
    this.showFullname = false;
    this.showLocation = false;
    this.showDOB = false;
    this.showGender = false;
    this.showEthnicity = false;
    this.showEmployment = false;
    this.showDesiredLocation = false;
    this.showEndPage = false;
  }

  onClickGetStartedBtn() {
    this.markStepCompleted(0);
    this.router.navigate(['/user-info/1']);
  }

  onNameContinue() {
    this.markStepCompleted(1);
    this.router.navigate(['/user-info/2']);
  }

  onLocationContinue() {
    this.markStepCompleted(2);
    this.router.navigate(['/user-info/3']);
  }

  onDOBContinue() {
    this.markStepCompleted(3);
    this.router.navigate(['/user-info/4']);
  }

  onGenderContinue() {
    this.markStepCompleted(4);
    this.router.navigate(['/user-info/5']);
  }

  onEthnicityContinue() {
    this.markStepCompleted(5);
    this.router.navigate(['/user-info/6']);
  }

  onEmploymentContinue() {
    this.markStepCompleted(6);
    this.router.navigate(['/user-info/7']);
  }

  onDesiredLocContinue() {
    this.markStepCompleted(7);
    analytics.track('BIQ Submit', {
      section: 'biq',
      location: this.storedBiqData.location,
      birthYear: this.storedBiqData.birthYear,
      gender: this.storedBiqData.gender,
      ethnicity: this.storedBiqData.ethnicity,
      grade: this.storedBiqData.grade,
      graduationYear: this.storedBiqData.graduationYear,
      school: this.storedBiqData.schoolName,
      major: this.storedBiqData.major,
      isWorking: this.storedBiqData.isWorking,
      jobType: this.storedBiqData.jobType,
      futureLocation: this.storedBiqData.futureLocation,
      futureLocationGooglePlaceId: this.storedBiqData.futureLocationGooglePlaceId,
      futureLocationAdministrativeAreaLevel2: this.storedBiqData.futureLocationAdministrativeAreaLevel2
    });
    this.biqApiService.saveUserInfo(
      this.getApiPayload()
    ).subscribe({
      next: () => {
        this.onSuccessSavingBiqData();
      },
      error: (error) => {
        console.error('Error saving user info:', error);
        this.showErrorMessage('Error while saving data. Please try again!');
      }
    });
  }

  getApiPayload() {
    return {
      first_name: this.storedBiqData.firstName,
      last_name: this.storedBiqData.lastName,
      location: this.storedBiqData.location,
      birth_year: this.storedBiqData.birthYear,
      gender: this.storedBiqData.gender,
      ethnicity: this.storedBiqData.ethnicity,
      school_grade: this.storedBiqData.grade,
      graduation_year: this.storedBiqData.graduationYear,
      school_name: this.storedBiqData.schoolName,
      graduation_major: this.storedBiqData.major,
      is_working: this.storedBiqData.isWorking,
      job_type: this.storedBiqData.jobType ?? null,
      future_location: this.storedBiqData.futureLocation,
      desired_location_google_place_id: this.storedBiqData.futureLocationGooglePlaceId ?? null,
      desired_location_google_administrative_area_level_2: this.storedBiqData.futureLocationAdministrativeAreaLevel2 ?? null,
    }
  }

  onSuccessSavingBiqData() {
    // Invalidate the /me cache so the next fetch gets the updated profile
    this.userService.invalidateMeCache();
    // Clear localStorage after successful API save
    this.clearLocalStorage();
    this.biqStorageService.clearCompletedSteps();
    this.completedSteps.clear();
    this.router.navigate(['/user-info-set']);
  }

  showErrorMessage(msg: string) {
    this._snackBar.openFromComponent(WarningSnackBarComponent, {
      ...WarningDefaultOptions,
      data: { 
        message: msg,
      },
      duration: 3000,
    });
  }

  onEndPageContinue() {
    this.router.navigate(['/survey']);
  }

  onScroll() {
    const el = this.contentDiv.nativeElement;
    el.classList.add('show-scrollbar');

    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      el.classList.remove('show-scrollbar');
    }, 1000);
  }

  navigateToStep(step: number) {
    if (step === 0) {
      this.router.navigate(['/user-info']);
    } else {
      this.router.navigate(['/user-info/' + step]);
    }
  }
  private getFirstIncompleteStep(targetStep: number): number | null {
    for (let i = 0; i < targetStep; i++) {
      if (!this.completedSteps.has(i)) {
        return i;
      }
    }
    return null;
  }

  private markStepCompleted(step: number) {
    this.completedSteps.add(step);
    this.biqStorageService.saveCompletedSteps([...this.completedSteps]);
  }

  private saveToLocalStorage(data: UserBiqData) {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(this.LOCALSTORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        console.warn('Failed to save user BIQ data to localStorage:', error);
      }
    }
  }

  private loadFromLocalStorage() {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const savedData = localStorage.getItem(this.LOCALSTORAGE_KEY);
        if (savedData) {
          const parsedData: UserBiqData = JSON.parse(savedData);
          this.store.dispatch(updateUserBiq({ user: parsedData }));
          console.log('Loaded user BIQ data from localStorage:', parsedData);
          
          // Force update of storedBiqData to ensure immediate availability
          this.storedBiqData = parsedData;
        }
      } catch (error) {
        console.warn('Failed to load user BIQ data from localStorage:', error);
        this.clearLocalStorage();
      }
    }
  }

  private clearLocalStorage() {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.removeItem(this.LOCALSTORAGE_KEY);
      } catch (error) {
        console.warn('Failed to clear user BIQ data from localStorage:', error);
      }
    }
  }

  public clearUserBiqData() {
    this.clearLocalStorage();
    this.biqStorageService.clearCompletedSteps();
    this.completedSteps.clear();
  }
}
